import { memo } from "react";
import { selectVisibleTabularViewColumns } from "~/features/search/redux/searchSelectors";
import { useDispatch, useSelector } from "react-redux";
import { changeSort } from "~/features/search/redux/actions";
import { useHSATableConfig } from "~/features/selectionAssistant/housing/components/HousingTabularView/HousingTabularViewConfig";
import { selectHSATabularViewData } from "~/features/selectionAssistant/housing/redux/selectors";
import { HousingMessages } from "~/domains/housing/components/HousingMessages";
import { HSA_TABLE_CONTAINER_JS_CLASS } from "~/features/selectionAssistant/housing/components/VariantProductsTableModal/HousingTabularViewDetailPanel";
import { CONTAINER_DESKTOP_L, CONTAINER_DESKTOP_M, CONTAINER_DESKTOP_S } from "~/common/utils/screenSizes";
import { calculateTotalTableWidth } from "~/features/search/components/ProductResults/views/TabularView/TanStackColumnDefs";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import { type TableState } from "@tanstack/react-table";
import { TableHoverHighlight } from "~/features/search/components/ProductResults/views/TabularView/TableHoverHighlight";
import { useTranslation } from "react-i18next";
import { SortDirection } from "~/features/search/models/models";
import { ResetSortBtn } from "~/features/selectionAssistant/bearing/components/Buttons/ResetSortBtn";
import { styled } from "~/common/mui";
import { OpenAbbreviationsBtn } from "~/features/selectionAssistant/bearing/components/AbbreviationListModal/AbbreviationListModal";
import { HSA_ATTRIBUTES } from "~/features/selectionAssistant/bearing/components/AbbreviationListModal/constants";
import { SIZE_DESKTOP_L, SIZE_DESKTOP_S, SIZE_TABLET, useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";

const ButtonList = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  flexDirection: "column",
  marginBottom: theme.spacing(3),
  [theme.breakpoints.up("tablet")]: {
    flexDirection: "row",
  },
  [theme.breakpoints.up("desktop-s")]: {
    marginBottom: theme.spacing(2),
  },
}));

const ResultsTitle = styled("h2")(({ theme }) => ({
  margin: 0,
  marginBottom: theme.spacing(2),
  ...theme.typography.titleMedium,
  [theme.breakpoints.up("desktop-s")]: {
    marginBottom: theme.spacing(3.75),
    ...theme.typography.headlineSmall,
  },
}));

const UnitsMessage = styled(HousingMessages.HSAUnits)(({ theme }) => ({
  marginBottom: theme.spacing(3.75),
  display: "flex",
}));

interface Props {
  totalNumberOfResults: number;
  submitHandler: () => unknown;
}

export const HSAResultsTable = memo(({ totalNumberOfResults }: Props) => {
  const size = useNumericScreenSize();
  const isMinTablet = size >= SIZE_TABLET;
  const isDesktopS = size >= SIZE_DESKTOP_S;
  const isDesktopM = size >= SIZE_DESKTOP_S;
  const isDesktopL = size >= SIZE_DESKTOP_L;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const config = useSelector(selectVisibleTabularViewColumns);

  const tanStackColumns = useHSATableConfig(size);

  const totalTableWidth = calculateTotalTableWidth(tanStackColumns);
  let containerSize = 0;
  if (isDesktopL) {
    containerSize = CONTAINER_DESKTOP_L;
  } else if (isDesktopM) {
    containerSize = CONTAINER_DESKTOP_M;
  } else if (isDesktopS) {
    containerSize = CONTAINER_DESKTOP_S;
  }
  const isTableLargerThanDesktop = totalTableWidth > containerSize;

  let state: Partial<TableState> = {};
  let fullwidth = true;
  if (!isMinTablet) {
    state = { columnPinning: { left: ["name"] } };
    fullwidth = false;
  } else if (!isDesktopS || isTableLargerThanDesktop) {
    state = { columnPinning: { left: ["name"], right: ["actions"] } };
    fullwidth = false;
  } else if (!isTableLargerThanDesktop) {
    // SCHAEFF-26787
    // Give all excess free space to the name column
    tanStackColumns[0].size = undefined;
  }

  const results = useSelector(selectHSATabularViewData);

  if (!config) {
    return null;
  }

  // TODO FIXME USE ICON

  let table = (
    <TanStackTable
      key={size}
      variant={"product"}
      data={results}
      columns={tanStackColumns}
      {...{ fullwidth, state }}
      stickyHeader={true}
    />
  );

  if (isDesktopS) {
    table = <TableHoverHighlight excludeLastCol={true}>{table}</TableHoverHighlight>;
  }

  return (
    <div>
      <ResultsTitle data-test-id={"selectionassistant-resultstitle"}>
        {t("page.selectionAssistant.productsHeader", {
          count: totalNumberOfResults,
        })}
      </ResultsTitle>
      <UnitsMessage className={"scha__hsa__units-msg"} />

      <ButtonList>
        <ResetSortBtn onClick={() => dispatch(changeSort("name", SortDirection.ASC))} />

        {!isDesktopS && <OpenAbbreviationsBtn attributes={HSA_ATTRIBUTES} />}
      </ButtonList>

      <div className={`scha__hsa__result-table ${HSA_TABLE_CONTAINER_JS_CLASS}`}>{table}</div>
    </div>
  );
});
